import { useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";

import { useIsFetching } from "@tanstack/react-query";

import type {
  Feature,
  FeatureCollection,
  GeoJsonProperties,
  Geometry
} from "geojson";

import { useGetAnalysisSummary } from "@/api/analysis/useAnalysisAPI";
import {
  useMutateGetAnalysisWidgetDefaultConfig,
  useQueryAnalysisWidgetConfig
} from "@/api/widget/useWidgetAPI";
import {
  CATCHMENT_ID_ATTRIBUTE,
  GRID_ID_ATTRIBUTE,
  POLYGON_ID_ATTRIBUTE
} from "@/components/analysisSummary/filterByGrid";
import NotificationContent from "@/components/notification/notificationContent";
import { DROPDOWN_ALL_VALUE } from "@/components/ui/dropdownMultiSelect";
import { SPATIAL_ANALYSIS_RESULT_DATASET_NAME } from "@/constants/dataset";
import { MESSAGE } from "@/constants/message";
import type { RootState } from "@/store";
import {
  resetGridsSelected,
  setGeneralSummary,
  setGridsSelected
} from "@/store/slice/analysisSummary";
import { addWidget, clearWidgets } from "@/store/slice/widgetSlice";
import type { GeneralSummary } from "@/types/analysisSummary";
import { WidgetDefaultType } from "@/types/widget";
import { getSourceProperties } from "@/utils/map";

import { useMap } from "../map/core/useMapContext";
import useToast, { EToastType } from "../ui/useToast";
import { useAppFeatures } from "../useAppFeatures";

const SUMMARY_FIT_ZOOM = 14;
const SUMMARY_OFFSET: [number, number] = [-150, 0];
const GENERATING_SUMMARY_TOAST_ID = "generating-summary-analysis-toast";

type UseAnalysisResultSummaryOptions = {
  // Forwarded to useGetAnalysisSummary. View Summary passes `false` and triggers imperatively via fetchSummary.
  enabled?: boolean;
  // Widget-mode panel opts in; gates the restore so the popup instance never triggers it.
  autoRestoreWidgetConfig?: boolean;
};

export const useAnalysisResultSummary = ({
  enabled = false,
  autoRestoreWidgetConfig = false
}: UseAnalysisResultSummaryOptions = {}) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { map, showOnlyLayer, handleMovePositionToLayer } = useMap();

  const { isDatasetWidgetEnabled } = useAppFeatures();
  const { latestAnalysisId } = useSelector((state: RootState) => state.spatial);
  const { datasets, visualizationOrder } = useSelector(
    (state: RootState) => state.dataset
  );
  const { gridsSelected } = useSelector(
    (state: RootState) => state.analysisSummary
  );

  const { mutateAsync: fetchWidgetDefaultConfig } =
    useMutateGetAnalysisWidgetDefaultConfig();

  const autoProcessedIdRef = useRef<string | null>(null);
  const restoreProcessedIdRef = useRef<string | null>(null);

  const shouldRestoreWidgetConfig =
    autoRestoreWidgetConfig && isDatasetWidgetEnabled && !!latestAnalysisId;

  // `latestAnalysisId` is the single source of truth — it's set when an
  // analysis runs and is guaranteed to exist before View Summary can be
  // clicked. Both fetchSummary and isFetching key off the same value, so
  // no extra Redux sync state is needed.
  const isFetchingOld =
    useIsFetching({ queryKey: ["analysis-summary", latestAnalysisId] }) > 0;

  // In widget mode, fetch waits for restore so highlightAnalysisResult reads the restored grid selection.
  const {
    data: autoFetchedSummary,
    isError: isAutoFetchError,
    isFetching,
    refetch: refetchAnalysisSummary
  } = useGetAnalysisSummary(latestAnalysisId ?? "", {
    enabled: enabled
  });

  const {
    data: savedWidgetConfig,
    isSuccess: isSavedWidgetConfigSuccess,
    isError: isSavedWidgetConfigError
  } = useQueryAnalysisWidgetConfig(latestAnalysisId ?? "", {
    enabled: shouldRestoreWidgetConfig
  });

  const highlightAnalysisResult = useCallback(
    (gridIds?: string[]) => {
      const analysisResultDataset = Object.values(datasets).find(
        (dataset) => dataset.data.name === SPATIAL_ANALYSIS_RESULT_DATASET_NAME
      );
      const analysisResultDatasetId = analysisResultDataset?.data.id;

      showOnlyLayer(analysisResultDatasetId as string);

      const featureData = getSourceProperties(
        map,
        analysisResultDatasetId as string
      ) as FeatureCollection<Geometry, GeoJsonProperties> | undefined;

      if (!featureData?.features) return;

      // Restored selection is authoritative — skip top-score auto-select for widget-mode instances.
      const hasSavedGrids =
        gridIds && gridIds.length > 0 && gridIds[0] !== DROPDOWN_ALL_VALUE;

      if (gridIds) {
        const savedFeatures = hasSavedGrids
          ? featureData.features.filter((f) =>
              gridIds.includes(
                f.properties?.[GRID_ID_ATTRIBUTE] ??
                  f.properties?.[CATCHMENT_ID_ATTRIBUTE] ??
                  f.properties?.[POLYGON_ID_ATTRIBUTE] ??
                  ""
              )
            )
          : [];

        // Empty gridIds means "all grids were selected" — restore the
        // DROPDOWN_ALL_VALUE sentinel so the dropdown renders "All" selected.
        dispatch(
          setGridsSelected(hasSavedGrids ? gridIds : [DROPDOWN_ALL_VALUE])
        );

        handleMovePositionToLayer(
          {
            type: "FeatureCollection",
            features:
              savedFeatures.length > 0 ? savedFeatures : featureData.features
          },
          { zoom: SUMMARY_FIT_ZOOM, offset: SUMMARY_OFFSET }
        );
        return;
      }

      const highestScoreFeature = featureData.features.reduce<
        Feature<Geometry, GeoJsonProperties>
      >(
        (acc, curr) => {
          return (curr?.properties?.["Score"] || 0) >
            (acc?.properties?.["Score"] || 0)
            ? curr
            : acc;
        },
        {} as Feature<Geometry, GeoJsonProperties>
      );

      dispatch(
        setGridsSelected([
          highestScoreFeature.properties?.[GRID_ID_ATTRIBUTE] ||
            highestScoreFeature.properties?.[CATCHMENT_ID_ATTRIBUTE] ||
            highestScoreFeature.properties?.[POLYGON_ID_ATTRIBUTE] ||
            DROPDOWN_ALL_VALUE
        ])
      );

      if (highestScoreFeature) {
        handleMovePositionToLayer(
          { type: "FeatureCollection", features: [highestScoreFeature] },
          { zoom: SUMMARY_FIT_ZOOM, offset: SUMMARY_OFFSET }
        );
      }
    },
    [datasets, dispatch, handleMovePositionToLayer, map, showOnlyLayer]
  );

  const applySummary = useCallback(
    (summary: GeneralSummary) => {
      dispatch(setGeneralSummary(summary));
      highlightAnalysisResult(gridsSelected);
    },
    [dispatch, gridsSelected, highlightAnalysisResult]
  );

  const showSummaryErrorToast = useCallback(() => {
    showToast({
      type: EToastType.ERROR,
      message: (
        <NotificationContent
          description={MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY_FAILED}
        />
      )
    });
  }, [showToast]);

  const fetchSummary = useCallback(
    async (analysisId: string) => {
      if (!analysisId) return;

      if (!isDatasetWidgetEnabled) {
        showToast({
          id: GENERATING_SUMMARY_TOAST_ID,
          type: EToastType.LOADING,
          message: (
            <NotificationContent
              description={MESSAGE.ANALYSIS_SUMMARY.GENERATING_SUMMARY}
            />
          )
        });
      }

      let summary: GeneralSummary;
      try {
        const { data, error } = await refetchAnalysisSummary();
        if (error || !data) throw error ?? new Error("No analysis summary");
        if (!isDatasetWidgetEnabled) {
          toast.dismiss(GENERATING_SUMMARY_TOAST_ID);
        }
        summary = data;
      } catch {
        if (!isDatasetWidgetEnabled) {
          toast.dismiss(GENERATING_SUMMARY_TOAST_ID);
        }
        showSummaryErrorToast();
        return;
      }

      applySummary(summary);
    },
    [
      applySummary,
      isDatasetWidgetEnabled,
      refetchAnalysisSummary,
      showSummaryErrorToast,
      showToast
    ]
  );

  // Process auto-fetched summary once per analysis; id guard prevents re-pan on later gridsSelected changes.
  useEffect(() => {
    if (
      !enabled ||
      !latestAnalysisId ||
      autoProcessedIdRef.current === latestAnalysisId
    ) {
      return;
    }

    if (autoFetchedSummary) {
      autoProcessedIdRef.current = latestAnalysisId;
      applySummary(autoFetchedSummary);
    } else if (isAutoFetchError) {
      autoProcessedIdRef.current = null;
      showSummaryErrorToast();
    }
  }, [
    enabled,
    latestAnalysisId,
    autoFetchedSummary,
    isAutoFetchError,
    applySummary,
    showSummaryErrorToast
  ]);

  // Restore widget config once per analysis; sets restoredAnalysisId to unblock the summary fetch.
  useEffect(() => {
    if (
      !shouldRestoreWidgetConfig ||
      !latestAnalysisId ||
      (!isSavedWidgetConfigSuccess && !isSavedWidgetConfigError) ||
      restoreProcessedIdRef.current === latestAnalysisId
    ) {
      return;
    }

    restoreProcessedIdRef.current = latestAnalysisId;
    const analysisId = latestAnalysisId;
    const config = savedWidgetConfig?.data ?? null;

    const restoreWidgetConfig = async () => {
      dispatch(clearWidgets());

      if (config?.widgets) {
        config.widgets.forEach(
          ({ group_by_attribute_id, grid_config: _, ...widget }) => {
            dispatch(
              addWidget({
                widget: {
                  ...widget,
                  is_default: WidgetDefaultType.USER_CREATED,
                  ...(group_by_attribute_id && {
                    group_by: group_by_attribute_id
                  })
                },
                id: widget.dataset_id
              })
            );
          }
        );
      } else {
        const results = await Promise.allSettled(
          visualizationOrder.map(async (datasetId) => {
            const response = await fetchWidgetDefaultConfig({
              analysisId,
              datasetId
            });
            for (const widget of response.data) {
              dispatch(addWidget({ widget, id: datasetId }));
            }
          })
        );
        results.forEach((result, i) => {
          if (result.status === "rejected") {
            console.error(
              `[useAnalysisResultSummary] Failed to fetch widget config for dataset ${visualizationOrder[i]}:`,
              result.reason
            );
          }
        });
      }

      // Dispatch grid selection AFTER widgets are in place so the persist effect
      // in FilterByAnalysisArea fires with complete widget state, not an empty one.
      if (config?.filter_by_catchment_ids) {
        highlightAnalysisResult(config.filter_by_catchment_ids);
      } else {
        // [] means "all grids were selected" — restore the DROPDOWN_ALL_VALUE sentinel
        dispatch(resetGridsSelected());
        highlightAnalysisResult();
      }

      // Yield to let React flush the grid-change render (with isRestoringWidgetConfig=true)
      // before turning the flag off, so the persist effect in FilterByAnalysisArea skips it.
      await Promise.resolve();
    };

    void restoreWidgetConfig();
  }, [
    shouldRestoreWidgetConfig,
    latestAnalysisId,
    isSavedWidgetConfigSuccess,
    isSavedWidgetConfigError,
    savedWidgetConfig,
    visualizationOrder,
    fetchWidgetDefaultConfig,
    dispatch,
    highlightAnalysisResult
  ]);

  return {
    fetchSummary,
    isFetching,
    isFetchingOld,
    isAutoFetchError,
    refetchAnalysisSummary
  };
};
